import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import { Button, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IPricingProgram } from '../../../Interfaces/Common/IPricingProgram';
import { IQuote, IQuoteDecisionData } from '../../../Interfaces/Common/IQuote';
import { Lookup } from '../../../Interfaces/StipulationTypes';
import AnalystCommentsEditor, { AnalystComment } from '../../Shared/AnalystCommentsEditor/AnalystCommentsEditor';
import DenialReasonsEditor from '../../Shared/DenialReasonsEditor/DenialReasonsEditor';
import { NumberFormatCustom } from '../../Shared/NumberFormatCustom';
import StipulationsEditor from '../../Shared/StipulationsEditor/StipulationsEditor';
import { DialogProps } from '../DialogProps';
import styles from './_indirect-decision-dialog.module.scss'
import { append, includes, isEmpty, sort } from 'ramda';
import { queryCache } from 'react-query';
import { PermissionSet } from '@amsanalytics/react-shared-library';
import { postData } from '../../../Data/fetch';
import { permissions } from '../../../utils/constants';
import { userHasPermission } from '../../../utils/utils';
import { IStipulationCategory } from '../../../Interfaces/StipulationTypes';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import { useDialog } from '../../../providers/DialogProvider';
import { IDealerServiceDecisionReasonCode } from '../../../Interfaces/Common/IDealerServiceDecisionCodeReason';
import { IDealerServiceDecisionCode } from '../../../Interfaces/Common/IDealerServiceDecisionCode';
import { useConfiguration } from '../../../Data/ConfigurationData';
import { sendDecisionToDealerTrackAndSaveApp } from '../../../store/application/actions';
import { Error } from '@progress/kendo-react-labels';
import { getApplicant, getAssignedEmployeeByRoleAndSequence, getIndirectSubmitToButtonText } from "../../../utils/appUtils";
import { calculateBalloonPayment, calculateLTV } from '../../../utils/quoteUtils';
import { hasValue } from "@amsanalytics/react-shared-library";
import { Constants } from '../../../utils/constants';
import { IApplicationAssignment } from '../../../Interfaces/Application/IApplicationAssignment';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import { IDecisionsSent } from '../../../Interfaces/Common/IDealerServiceAppHistory';
import { useApplicationCustomerStipulations } from '../../../Data/StipulationData';
import ErrorDialog from '../ErrorDialog';
import { usePricingPrograms } from '../../../Data/LookupData';
import { IndirectQuoteType } from '../../../Data/IndirectQuoteType';
import { getAppQuoteByType } from '../../Quoters/IndirectQuoter/IndirectQuoterUtils';
import { loadIndirectStipsAndDecisionReasons, setDealerServiceDecisionCode } from '../../../store/dealerService/actions';
import {IDealerDecisionData} from "../../../Interfaces/Common/IDealerDecisionData";
import { buildDecisionSummary, withDefaultComment } from '../../../utils/decisionDialogUtils';

const STIPULATION_REQUIRED_MESSAGE = 'A stipulation is required for this decision code';
const REASON_REQUIRED_MESSAGE = 'A denial reason is required';
const INLINE_ERROR_MESSAGES = [STIPULATION_REQUIRED_MESSAGE, REASON_REQUIRED_MESSAGE];

export interface IndirectDealerApplicationData {
  decisionCode: string;
  decisionCodeId?: string;
  analystComments: AnalystComment[];
  denialReasons?: IDealerServiceDecisionReasonCode[];
  stipulations?: Lookup[];
}

export interface ApplicationStipulationDocument {
  // Define the structure if needed
}

export interface IndirectDealerDecisionDialogProps extends DialogProps<IndirectDealerApplicationData> {
  dealerTrackApplicationId: string;
  dealerName: string;
  nextStatusId: string;
  applicationDate: Date;
  quotes: IQuoteDecisionData[];
  pricingPrograms?: IPricingProgram[];
  currentDealerServiceDecisionCode: IDealerServiceDecisionCode | null;
  handleSaveAppFromOverview?: (currentApp: IApplication, originalApplicationStatus: string, bypassAppRequirements: boolean) => void;
  indirectStipulationDynamicTextDictionary: { [key: number]: string };
}

const validatePreDealertrackDecision = (
  quote: IQuote,
  applicationAssignments: IApplicationAssignment[],
  ficoScore?: number | undefined,
): string[] => {    
  const errors: string[] = [];
  if (!hasValue(quote.amountToFinance)) {
    errors.push('Amount to Finance is required');
  }
  if (!hasValue(quote.pricingProgramId)) {
    errors.push('Pricing Program is required');
  }
  if (!hasValue(quote.apr) || quote.apr === 0) {
    errors.push('Rate is required');
  }
  if (!hasValue(ficoScore)) {
    errors.push('Fico score is required');
  }
  if (!hasValue(quote?.term)) {
    errors.push('Term is required');
  }
  if (!hasValue(getAssignedEmployeeByRoleAndSequence(applicationAssignments, Constants.roles.underwriterId, 1)?.assignedToId)) {
    errors.push('Underwriter is required');
  }

  return errors;
};
export const useDecisionDialog = (
  application: IApplication
  ) => {
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch();
  const dealerServiceAppHistoryItems = useSelector((state: AppState) => state.applications.dealerServiceAppHistoryItems);
  const indirectStipulationDynamicTextDictionary = useSelector((state: AppState) => state.dealerService.indirectStipulationDynamicTextDictionary);
  const decisionCodes = useSelector((state: AppState) => state.applications.lookups.dealerServiceDecisionCodes);
  const applicationAssignments: IApplicationAssignment[] = useSelector((state: AppState) => state.applications.editingApp?.applicationAssignments) ?? [];
  const { data: pricingPrograms } = usePricingPrograms();
  const underwriterQuote = application.applicationQuotes.find(aq => aq.quote.quoteType === IndirectQuoteType.Underwriter)?.quote;
  const getDecisionIdFromCode = (decisionCode: string) => {
    return decisionCodes.find(dc => dc.code === decisionCode && dc.appSource === application.appSource) ?? null;
  };
  const ficoScore = getApplicant(application, Constants.roleApplicantId, true)?.customer?.customerCredit?.[0]?.bureauScore;
  const ltvRatio = underwriterQuote 
    ? (calculateLTV(underwriterQuote, application.vehicle, false) as number ?? 0) 
    : 0;
  const tierLevel = pricingPrograms?.find(x => x.id === underwriterQuote?.pricingProgramId)?.name;
  const preDealertrackDecisionErrors: string[] = underwriterQuote 
    ? validatePreDealertrackDecision(underwriterQuote, applicationAssignments, ficoScore)
    : [];

  const openDecisionDialog = async (nextStatusId: string) => {
    if (application && (nextStatusId?.toUpperCase() === Constants.applicationStatuses.denied || preDealertrackDecisionErrors.length === 0)) {
      if (!underwriterQuote) {
        return;
      }
      
      openDialog({
        fullScreen: false,
        children: (
          <IndirectDealerDecisionDialog
            title={`${application.appSource} Decision`}
            applicationDate={application.applicationDate}
            nextStatusId={nextStatusId}
            indirectStipulationDynamicTextDictionary={indirectStipulationDynamicTextDictionary}
            currentDealerServiceDecisionCode={
              !isEmpty(dealerServiceAppHistoryItems.decisionsSent)
                ? getDecisionIdFromCode(sort((left: IDecisionsSent, right: IDecisionsSent) =>
                  new Date(left.createdDate) > new Date(right.createdDate) ? -1
                    : new Date(left.createdDate) < new Date(right.createdDate) ? 1
                      : 0
                  , dealerServiceAppHistoryItems.decisionsSent
                )[0].decisionCode)
                : null
            }
            quotes={
              [
                ...(application.applicationQuotes.map(aq => {
                  return {
                    ...aq.quote,
                    useVehiclePrice: aq.useVehiclePrice,
                    bankFee: aq.quote.bankFee ?? 0,
                    tradeAllowance: aq.quote.tradeAllowance ?? 0,
                    balloonPayment: calculateBalloonPayment(aq.quote) ?? 0,
                    carSalePrice: aq.quote.carSalePrice ?? 0,
                    quotePayTypeId: aq.quote.quotePayTypeId
                  }
                }))
              ]
            }
            dealerName={""}
            dealerTrackApplicationId={""}
            pricingPrograms={pricingPrograms}
            accept={async (decisionInfo: IndirectDealerApplicationData) => {
              if (application) {
                const decisionData: IDealerDecisionData = {
                  applicationId: application.id,
                  underwriterId: application.applicationAssignments.find(a => a.assignmentEmployeeRoleId === Constants.roles.underwriterId)?.assignedToId!,
                  buyRate: underwriterQuote.apr!,
                  ltvRatio: ltvRatio ?? 0,
                  ficoScore: ficoScore!,
                  amountToFinance: underwriterQuote.amountToFinance!,
                  tierLevel: tierLevel!,
                  termInMonths: underwriterQuote.term,
                  acquisitionFee: underwriterQuote.bankFee,
                  decisionCode: decisionInfo.decisionCode,
                  decisionCodeId: decisionInfo.decisionCodeId,
                  analystComments: decisionInfo.analystComments,
                  balloonPayment: calculateBalloonPayment(underwriterQuote) ?? 0,
                  payment: underwriterQuote.payment!,
                  cashDown: underwriterQuote.cashDown,
                  tradeAllowance: underwriterQuote.tradeAllowance,
                  carSalePrice: underwriterQuote.carSalePrice,
                  paymentFrequency: underwriterQuote?.quotePayTypeId,
                  conditionedOrDeclinedReasons: decisionInfo.denialReasons?.map((dn, index) => {
                    return {
                      id: dn.id,
                      lineNumber: index + 1,
                      text: dn.description
                    }
                  }),
                  stipulations: decisionInfo.stipulations?.map((s, index) => {
                    return {
                      id: s.id,
                      lineNumber: index + 1,
                      text: indirectStipulationDynamicTextDictionary[s.id] ?? s.name,
                      hasDynamicText: indirectStipulationDynamicTextDictionary[s.id] !== undefined,
                    }
                  }),
                };
                
                dispatch(sendDecisionToDealerTrackAndSaveApp(decisionData, application, false));
              }
              closeDialog();
            }}
            closeDialog={closeDialog}
          />
        )
      });
    } else {
      openDialog({
        children: (
          <ErrorDialog
            title="Decision Validation Errors"
            errors={preDealertrackDecisionErrors}
            closeDialog={closeDialog}
          />
        ),
      });
    }
  };

  return [openDecisionDialog];
}

export default function IndirectDealerDecisionDialog(props: IndirectDealerDecisionDialogProps) {
  const [analystComments, setAnalystComments] = useState<AnalystComment[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<IDealerServiceDecisionReasonCode[] | undefined>(undefined);
  const [selectedStipulations, setSelectedStipulations] = useState<Lookup[]>();
  const [decisionErrors, setDecisionErrors] = useState<string[]>([]);
  const [openDialog, closeDialog] = useDialog();
  const decisionComments = useSelector((state: AppState) => state.dealerService.decisionComments);
  const payTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const decisionCode = useSelector((state: AppState) => state.dealerService.decisionCode);
  const { data: stipulationData } = useApplicationCustomerStipulations(currentApp ? currentApp.id : Constants.emptyGuid);
  const { data: configuration } = useConfiguration();
  const appStatuses = useSelector((state: AppState) => state.applications.appStatuses);
  const dispatch = useDispatch();
  const indirectStipulations = useSelector((state: AppState) => state.dealerService.applicationStipulations);
  const indirectDecisionReasons = useSelector((state: AppState) => state.dealerService.adverseActionReasons);
  const decisionCodeDecisionReasonDefaults = useSelector((state: AppState) => state.dealerService.decisionCodeDecisionReasonDefaults);
  const loggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const canAddCustomStip = !!loggedInEmployee && userHasPermission(loggedInEmployee, permissions.addCustomStipulations, PermissionSet.C);

  // Creates the custom stip category, refreshes the category list (the checkbox list is derived from
  // the react-query data every render, so a refetch is enough), and checks the new entry.
  const handleAddCustomStip = async (name: string) => {
    if (!currentApp) {
      return;
    }
    const created = await postData('api/Stipulations/customCategory', { name }) as IStipulationCategory;
    await queryCache.refetchQueries(['ApplicationCustomerStipulations', currentApp.id], { force: true });
    setSelectedStipulations(previous => append({
      id: created.id,
      name: created.name,
      description: created.description ?? '',
      sortOrder: created.sortOrder ?? 0,
      isCustom: true,
    }, previous ?? []));
  };

  useEffect(() => {
    if(currentApp) {
      const underwriterQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter);
      if(underwriterQuote) {
        getInitialComments(underwriterQuote as any as  IQuoteDecisionData);
      }
      const decisionCodesForAppSource = lookups.dealerServiceDecisionCodes.filter(dc => dc.appSource === currentApp?.appSource);
      const nextDecisionCode = decisionCodesForAppSource.find(dc => dc.appStatusId?.toLowerCase() === props.nextStatusId.toLowerCase())?.code ?? '';
      dispatch(setDealerServiceDecisionCode(nextDecisionCode) as any);

      dispatch(loadIndirectStipsAndDecisionReasons(currentApp.id))
    }
  }, [currentApp]);

  const getValidationStatus = () => {
    const validated = currentApp?.applicationCustomFields?.find(x => x.customField?.name === 'Funding Validated')?.value ?? '';
    const overridden = currentApp?.applicationCustomFields?.find(x => x.customField?.name === 'Funding Overridden')?.value ?? '';

    if (validated !== '') {
      return validated;
    } else if (overridden !== '') {
      return overridden;
    }
    return '';
  }

  const stipulationCategories: Lookup[] = [];
  stipulationData?.stipulationCategoryTypes.forEach(st => st.stipulationCategories.forEach((sc, index) => {
      stipulationCategories.push({
        id: sc.id,
        name: sc.name,
        description: sc.description,
        sortOrder: sc.sortOrder,
        isCustom: sc.isCustom
      });
    }));

  // Convert indirectStipulations to Lookup[] format
  useEffect(() => {
    if (indirectStipulations && indirectStipulations.length > 0 && !selectedStipulations) {
      const convertedStips: Lookup[] = indirectStipulations.map(stip => ({
        id: stip.stipulationCategoryId,
        name: stip.name,
        description: stip.dynamicText || "",
        sortOrder: stip.sequenceNumber 
      }));
      
      setSelectedStipulations(convertedStips);
    } 

  }, [indirectStipulations, decisionCode, lookups.dealerServiceDecisionCodes, currentApp]);

useEffect(() => {
  if (currentApp) {
    // If there are existing indirect decision reasons, use them
    if (indirectDecisionReasons && indirectDecisionReasons.length > 0) {
      const reasons: IDealerServiceDecisionReasonCode[] = 
        indirectDecisionReasons.map((reason: any) => {
          return {
            id: reason.dealerServiceDecisionReasonCodeId || '',
            code: reason.id || '',
            description: reason.name || '',
            sortOrder: reason.sequenceNumber || 0
          };
        });

      setSelectedReasons(reasons);
    } 
    else if (decisionCodeDecisionReasonDefaults && decisionCodeDecisionReasonDefaults.length > 0) {

      const decisionCodesForAppSource = lookups.dealerServiceDecisionCodes.filter(dc => dc.appSource === currentApp?.appSource);
      const nextDecisionCode = decisionCodesForAppSource.find(dc => dc.appStatusId?.toLowerCase() === props.nextStatusId.toLowerCase())?.id ?? '';

      const matchingDefaults = decisionCodeDecisionReasonDefaults.filter(
        defaultItem => defaultItem.decisionCodeId === nextDecisionCode
      );
      
      // Get the corresponding decision reason codes from lookups
      const defaultReasons: IDealerServiceDecisionReasonCode[] = matchingDefaults
        .map(defaultItem => {
          // Find the matching decision reason code in lookups
          return lookups.dealerServiceDecisionReasonCodes.find(
            reasonCode => reasonCode.id === defaultItem.decisionReasonId
          );
        })
        .filter((reasonCode): reasonCode is IDealerServiceDecisionReasonCode => 
          reasonCode !== undefined
        ); // Remove any undefined results
      
      setSelectedReasons(defaultReasons);
    }
  }
}, [indirectDecisionReasons, decisionCodeDecisionReasonDefaults, props.nextStatusId, lookups.dealerServiceDecisionReasonCodes]);



  const status = appStatuses.find(s => s.id === props.nextStatusId)?.name ?? 'ERROR';
  const isDenial = props.nextStatusId?.toUpperCase() === Constants.applicationStatuses.denied;

  const stipsRequired = !!lookups.dealerServiceDecisionCodes
    && includes(decisionCode, lookups.dealerServiceDecisionCodes.filter(dc => dc.stipsRequired).map(dc => dc.code));
  const reasonsRequired = !!lookups.dealerServiceDecisionReasonCodes && !!lookups.dealerServiceDecisionCodes
    && includes(decisionCode, lookups.dealerServiceDecisionCodes.filter(dc => dc.reasonsRequired).map(dc => dc.code));
  const defaultComment = (decisionComments && decisionCode) ? decisionComments[decisionCode] : undefined;

  const decisionSummary = buildDecisionSummary({
    stipulationCount: stipsRequired ? (selectedStipulations ?? []).length : undefined,
    reasonCount: reasonsRequired ? (selectedReasons ?? []).length : undefined,
    commentCount: withDefaultComment(analystComments, defaultComment).length,
  });

  // List-specific errors render inline in their card header; anything else stays in the footer.
  const stipulationError = decisionErrors.find(e => e === STIPULATION_REQUIRED_MESSAGE);
  const reasonError = decisionErrors.find(e => e === REASON_REQUIRED_MESSAGE);
  const footerErrors = decisionErrors.filter(e => !INLINE_ERROR_MESSAGES.includes(e));

  const validateDecision = (): string[] => {
    const errors: string[] = [];

    if (!hasValue(decisionCode)) {
      errors.push('Decision Code is required');
    }
    if (stipsRequired && (!selectedStipulations || selectedStipulations.length === 0)) {
      errors.push(STIPULATION_REQUIRED_MESSAGE);
    }
    if (reasonsRequired && (!selectedReasons || selectedReasons.length === 0)) {
      errors.push(REASON_REQUIRED_MESSAGE);
    }
    if (configuration?.clientId === 'RDF' && (decisionCode === 'OT-Fun' || decisionCode === 'FU') && getValidationStatus() === '') {
      errors.push('Funding must be Validated or Overridden before sending this status.');
    }

    return errors;
  };

  const getInitialComments = (underwriterQuote: IQuoteDecisionData) => {
    let comments: string[] = [];
    if (underwriterQuote.balloonPayment > 0) {
      comments.push(`BALLOON PAYMENT ${underwriterQuote.balloonPayment.toFixed(2)}`);
    }
    if (underwriterQuote.carSalePrice && underwriterQuote.carSalePrice > 0 && configuration?.featureFlags?.[FeatureFlagIds.AddCarSalesPriceComment] === true) {
      comments.push(`CAR SALES PRICE ${underwriterQuote.carSalePrice.toFixed(2)}`);
    }

    if (comments.length > 0) {
      let initialComments: AnalystComment[] = [
        {
          type: "credit",
          comments: comments.join(' - ')
        }
      ];

      if (initialComments && initialComments.length > 0) {
        setAnalystComments(initialComments);
      }
    }
  }

  const getColumnHeaderText = (key: number): string => {
    if (key === 0) {
      return 'Dealer Request' // currentApp?.appSource ?? 'Quote';
    }

    if (key === 1) {
      return 'Recommended';
    }

    return 'Underwrite Quote';
  }

  return (
    <>
      <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
        {`Submit to ${getIndirectSubmitToButtonText(currentApp?.appSource)}`}
      </DialogTitle>
      <DialogContent style={{ width: 'calc(100vw - 200px)' }}>
        <div className={styles.indirectDecisionDialogContent}>
          <div className={styles.decisions}>
            <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
              <div className={styles.indirectDecisionChoices}>
                {[...props.quotes].sort((a: IQuoteDecisionData, b: IQuoteDecisionData) => b.quoteType! - a.quoteType!).map((quote, key) => (
                  <div className={styles.quote} key={key} style={{ maxWidth: '350px' }}>
                    <div className={styles.header}>

                      <div className={styles.title}>
                        {getColumnHeaderText(key)}{props.dealerTrackApplicationId}
                      </div>
                    </div>
                    <Grid container spacing={1}>
                      {/* Pricing Program */}
                      {props.pricingPrograms && (
                        <Grid item xs={12}>
                          <TextField
                            style={{ textAlign: 'right' }}
                            label='Pricing Program'
                            value={props.pricingPrograms.find((pp) => pp.id === quote.pricingProgramId)?.name || ''}
                            fullWidth
                            size='small'
                            variant='outlined'
                            inputProps={{ style: { textAlign: 'right' } }}
                            disabled={true}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          label='Amount to Finance'
                          value={quote.amountToFinance || ''}
                          fullWidth
                          size='small'
                          variant='outlined'
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                      {/* Down Payment  */}
                      <Grid item xs={12}>
                        <TextField
                          label='Cash Down'
                          value={quote.cashDown || 0}
                          fullWidth
                          size='small'
                          variant='outlined'
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                      {/* LTV */}
                      <Grid item xs={12}>
                        <TextField
                          label='LTV'
                          style={{ visibility: quote.useVehiclePrice ? 'visible' : 'hidden' }}
                          value={calculateLTV(quote, currentApp?.vehicle, true)}
                          fullWidth
                          size='small'
                          variant='outlined'
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                      {/* Term */}
                      <Grid item xs={12}>
                        <TextField
                          select
                          label='Pay Type'
                          value={quote.quotePayTypeId || ''}
                          fullWidth
                          size='small'
                          variant='outlined'
                          style={{ visibility: quote.quotePayTypeId !== undefined ? 'visible' : 'hidden' }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        >
                          <option>&nbsp;</option>
                          {payTypes.map((x, key) => (
                            <option key={key} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                        </TextField>
                      </Grid>
                      {/* Term */}
                      <Grid item xs={12}>
                        <TextField
                          label='Term (months)'
                          value={quote.term || ''}
                          fullWidth
                          size='small'
                          variant='outlined'
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                      {/* Balloon Payment */}
                      <Grid item xs={12}>
                        <TextField
                          label='Balloon Payment'
                          value={quote.balloonPayment || ''}
                          fullWidth
                          size='small'
                          variant='outlined'
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                        />
                      </Grid>
                      {/* Bank Fee */}
                      <Grid item xs={12}>
                        <TextField
                          label='Aquisition Fee'
                          value={quote.bankFee || ''}
                          style={{ visibility: quote.bankFee !== undefined ? 'visible' : 'hidden' }}
                          fullWidth
                          size='small'
                          variant='outlined'
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                      {/* Trade Allowance */}
                      <Grid item xs={12}>
                        <TextField
                          label='Trade Allowance'
                          value={quote.tradeAllowance || ''}
                          fullWidth
                          size='small'
                          variant='outlined'
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                      {/* Rate */}
                      <Grid item xs={12}>
                        <TextField
                          label='Rate'
                          value={quote.apr || ''}
                          fullWidth
                          size='small'
                          variant='outlined'
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                      {/* Payment */}
                      <Grid item xs={12}>
                        <TextField
                          label='Payment'
                          value={quote.payment || ''}
                          fullWidth
                          size='small'
                          variant='outlined'
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>

                      {/* Dealer Check */}
                      <Grid item xs={12}>
                        <TextField
                          label='Dealer Check'
                          value={
                            quote.dealerCheck
                              ? quote.dealerCheck : 0
                          }
                          fullWidth
                          size='small'
                          variant='outlined'
                          InputProps={{
                            inputComponent: NumberFormatCustom as any,
                          }}
                          inputProps={{ style: { textAlign: 'right' } }}
                          disabled={true}
                        />
                      </Grid>
                    </Grid>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.indirectDecisionOptions}>
              <div className={styles.decisionHeader}>
                <span className={styles.decisionLabel}>Decision</span>
                <span className={`${styles.statusPill} ${isDenial ? styles.statusPillDenied : styles.statusPillApproved}`}>{status}</span>
                <span className={styles.spacer} />
                <span className={styles.decisionSummary}>{decisionSummary}</span>
              </div>
              {/* A code that needs both lists shows them side by side so both stay on screen above the comments. */}
              <div className={stipsRequired && reasonsRequired ? styles.listsSideBySide : styles.listsStacked}>
                {stipsRequired && (
                  <StipulationsEditor
                    onChange={(stipulationCategories: Lookup[]) => {
                      setSelectedStipulations(stipulationCategories);
                    }}
                    compact={reasonsRequired}
                    errorMessage={stipulationError}
                    selectedItems={selectedStipulations!}
                    stipulationCategories={stipulationCategories}
                    indirectStipulationDynamicTextDictionary={props.indirectStipulationDynamicTextDictionary}
                    canAddCustomStip={canAddCustomStip}
                    onAddCustomStip={handleAddCustomStip}
                  />
                )}
                {reasonsRequired && (
                  <DenialReasonsEditor
                    onChange={(reasons: IDealerServiceDecisionReasonCode[]) => {
                      setSelectedReasons(reasons);
                    }}
                    compact={stipsRequired}
                    errorMessage={reasonError}
                    selectedItems={selectedReasons ?? []}
                    reasons={lookups.dealerServiceDecisionReasonCodes}
                  />
                )}
              </div>
              <AnalystCommentsEditor
                onChange={(comments: AnalystComment[]) => {
                  setAnalystComments(comments);
                }}
                initialComments={analystComments}
                pinnedComment={defaultComment}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        {footerErrors.length > 0 && (
          <Error>{footerErrors.join(' · ')}</Error>
        )}
        <Button autoFocus
          onClick={() => {
            closeDialog();
          }}
          color='primary'
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            // Appended here, not into state, so a failed validation cannot add the default comment twice.
            const commentsToSend = withDefaultComment(analystComments, defaultComment);

            const errors: string[] = validateDecision();
            setDecisionErrors(errors);
            if (errors.length < 1) {
              // Get the decision code ID from the lookups based on code and appSource
              const decisionCodeId = lookups.dealerServiceDecisionCodes.find(
                dc => dc.code === decisionCode && dc.appSource === currentApp?.appSource && dc.appStatusId?.toLowerCase() === props.nextStatusId.toLowerCase()
              )?.id;
              decisionCode && props.accept && props.accept({
                decisionCode,
                decisionCodeId,
                analystComments: commentsToSend,
                denialReasons: (selectedReasons ?? []).length > 0 ? selectedReasons : undefined,
                stipulations: (selectedStipulations ?? []).length > 0 ? selectedStipulations : undefined,
              });
            }
          }}
          color='primary'
        >
          Submit
        </Button>
      </DialogActions>
    </>
  );
}